import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import RecordCount from "../../../assets/SharedComponents/RecordCount/RecordCount";
import { Button, Tooltip } from "@mui/material";
import { ThreeDots } from "react-loader-spinner";
import { Wrapper } from "./style/style";
import { confirmAlert } from "react-confirm-alert";
import API from "../../../api/axios";
import { APP_URLS } from "../../../api/url";
import { APIError, showUserTypeBadge } from "../../../core/utils";
import { FaFilterIcon } from "../../../assets/Images/icon";
import NoRecord from "../../../assets/SharedComponents/Table/NoRecord";
import Pagination from "../../../assets/SharedComponents/Pagination";
import { Link, useLocation } from "react-router-dom";
import { CHATREPORTSTATUS, userTypes } from "../../../config/constant";
import StaticFilter from "../../../assets/SharedComponents/Filters/StaticFilter";

const ReportStatus = Object.keys(CHATREPORTSTATUS).map((key) => ({
  name: key,
  value: CHATREPORTSTATUS[key],
}));

const ReportedChats = () => {
  const userAccess = JSON.parse(localStorage.getItem("permissions"));

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const [chats, _chats] = useState([]);
  const [page, setPage] = useState(Number(searchParams.get("page") || 1));
  const [total, _total] = useState(0);
  const [totalPage, _totalPage] = useState(0);

  const [status, _status] = useState(
    Number(searchParams.get("status") || CHATREPORTSTATUS.Pending)
  );
  const [filters, _filters] = useState(false);

  const [countValue, _countValue] = useState(
    Number(searchParams.get("per_page") || 10)
  );

  const [chatsLoader, _chatsLoader] = useState(false);
  const [actionloader, _actionloader] = useState(false);

  const reportedChats = useMemo(
    () => userAccess.find((o) => o.slug === "reported_users"),
    [userAccess]
  );

  const readMessages = useMemo(
    () => userAccess.find((o) => o.slug === "read_messages"),
    [userAccess]
  );

  const userManagement = useMemo(
    () => userAccess.find((o) => o.slug === "users"),
    [userAccess]
  );

  const canCensorUser = useMemo(() => {
    return userManagement?.permissions?.some((perm) => perm.slug === "update");
  }, [userManagement]);

  const canReadMessages = useMemo(() => {
    return readMessages?.permissions?.some((perm) => perm.slug === "read");
  }, [readMessages]);

  const canDeleteReportedChats = useMemo(() => {
    return reportedChats?.permissions?.some((perm) => perm.slug === "delete");
  }, [reportedChats]);

  useEffect(() => {
    getReportedChats();
  }, [page, countValue, status]);

  const getReportedChats = () => {
    _chatsLoader(true);
    API.get(
      `${APP_URLS.REPORT_CHATS}?page=${page}&per_page=${countValue}&status=${status}}`
    )
      .then((res) => {
        const resp = res.data;
        if (resp.success === true) {
          _chats(resp.data.data);
          _total(resp.data.total);
          _totalPage(resp.data.last_page);
        } else {
          toast.error(resp.message, {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch(function (error) {
        let error_message = APIError(error);
        toast.error(error_message, {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .finally(() => {
        _chatsLoader(false);
      });
  };

  const handleChange = (searchValue) => {
    _countValue(searchValue);
    _totalPage(0);
    setPage(1);
  };

  //removing all filters
  const clearFilter = () => {
    _filters(true);
    _status(CHATREPORTSTATUS.Pending);
    _countValue(10);
    _totalPage(0);
    setPage(1);
  };

  const handleAction = (chatID) => {
    if (actionloader) return;
    confirmAlert({
      title: `Confirm to reject`,
      message: `Are you sure you want to reject this request?`,
      closeOnEscape: true,
      closeOnClickOutside: true,
      overlayClassName: "overlay-custom-class-name",
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            _actionloader(true);
            API.delete(`${APP_URLS.DELETE_REPORT_CHATS}/${chatID}`, {
              status: 2,
            })
              .then((res) => {
                const resp = res.data;
                if (resp.success === true) {
                  toast.success(`Reported chat request rejected successfully`, {
                    position: toast.POSITION.TOP_RIGHT,
                  });
                  getReportedChats();
                } else {
                  toast.error(resp.message, {
                    position: toast.POSITION.TOP_RIGHT,
                  });
                }
              })
              .catch(function (error) {
                let error_message = APIError(error);
                toast.error(error_message, {
                  position: toast.POSITION.TOP_RIGHT,
                });
              })
              .finally(() => {
                _actionloader(false);
              });
          },
        },
        { label: "No" },
      ],
    });
  };

  return (
    <>
      <Wrapper>
        <div className="auctionMainContainer">
          <div className="row mt-4">
            <div className="col-md-12 text-align-center">
              <h1>REPORTED CHATS</h1>
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-end info-wrap mt-3 mb-2 gap-2">
            <StaticFilter
              placeholder="Report status"
              data={ReportStatus}
              handleChange={(e) => {
                _status(e.target.value);
                _totalPage(0);
                setPage(1);
              }}
              value={status}
            />
            <RecordCount
              onSearchClick={(search) => handleChange(search)}
              id="pageCount"
              val={countValue}
              filters={filters}
              _filters={_filters}
            />
            <Tooltip title="Clear Filter" placement="bottom">
              <FaFilterIcon
                className="filter-icon"
                role="button"
                onClick={clearFilter}
              />
            </Tooltip>
          </div>
          <div className="d-flex justify-content-between mt-2 me-2 w-100">
            <span className="text-start">
              Total Records : <b>{total || "0"}</b>
            </span>
          </div>
          <div className="tableContainer">
            <table className="auctionTable">
              <thead className="tableHead">
                <tr className="table-row">
                  <th className="idColumn">ID</th>
                  <th className="idColumn">Chat ID</th>
                  <th className="nameColumn">Reported</th>
                  <th className="nameColumn">Reported by</th>
                  <th className="reasonColumn">Reason</th>
                  {canReadMessages && <th>View chat</th>}
                  <th>Action</th>
                </tr>
              </thead>
              {chatsLoader ? (
                <div className="d-flex justify-content-center three-dot">
                  <ThreeDots
                    height="150"
                    width="150"
                    radius="9"
                    color="#637df4"
                    ariaLabel="three-dots-loading"
                    wrapperStyle={{}}
                    wrapperClassName=""
                    visible={true}
                  />
                </div>
              ) : (
                <tbody>
                  {chats.length > 0 ? (
                    chats.map((chat) => {
                      return (
                        <tr className={`${chat.deleted_at ? "deletedRow" : ""} table-row`} key={`reported-chat-${chat.id}`}>
                          <td className="idColumn">{chat.id}</td>
                          <td className="idColumn">{chat.chat_id}</td>
                          <td className="nameColumn">
                            <a
                              href={`${chat.reported_subsidiary_id
                                ? "/company-details"
                                : "/detail-member"
                                }/${chat.reported_subsidiary_id ??
                                chat.reported_user_id
                                }`}
                              className="link"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <b>
                                {chat.reported_name}
                                {showUserTypeBadge(
                                  chat.reported_subsidiary_id
                                    ? "subsidiary"
                                    : ""
                                )}
                              </b>
                            </a>
                            {canReadMessages && <Link to={`/chats/${chat.reported_user_id ? userTypes.Private : userTypes.Dealer}/${chat.reported_user_id ?? chat.reported_subsidiary_id}`} className="viewChats">
                              View Chats
                            </Link>}
                          </td>
                          <td className="nameColumn">
                            <a
                              href={`${chat.reported_by_subsidiary_id
                                ? "/company-details"
                                : "/detail-member"
                                }/${chat.reported_by_subsidiary_id ??
                                chat.reported_by_user_id
                                }`}
                              className="link"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <b>
                                {chat.reported_by_name}
                                {showUserTypeBadge(
                                  chat.reported_by_subsidiary_id
                                    ? "subsidiary"
                                    : ""
                                )}
                              </b>
                            </a>
                          </td>
                          <td className="reasonColumn">{chat.reason_text}</td>
                          {
                            canReadMessages && (
                              <td>
                                <Link
                                  to={`/chats-messages/${chat.chat_id}?page=${page}&per_page=${countValue}&status=${status}`}
                                  className="link"
                                >
                                  View
                                </Link>
                              </td>
                            )
                          }
                          <td className="action">
                            {chat.status === CHATREPORTSTATUS.Rejected ? (
                              <span className="reject disabled">Rejected</span>
                            ) : chat.status === CHATREPORTSTATUS.Accepted ? (
                              <span className="accept disabled">Accepted</span>
                            ) : (
                              <>
                                {canCensorUser && (
                                  <a
                                    href={`${chat.reported_subsidiary_id
                                      ? "/company"
                                      : "/user"
                                      }/create-censored/${chat.reported_subsidiary_id ??
                                      chat.reported_user_id
                                      }?reported=true`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="accept"
                                  >
                                    Accept
                                  </a>
                                )}

                                {canDeleteReportedChats && (
                                  <button
                                    className="reject"
                                    onClick={() => handleAction(chat.id)}
                                    disabled={actionloader}
                                  >
                                    Reject
                                  </button>
                                )}
                              </>
                            )}
                          </td>
                        </tr >
                      );
                    })
                  ) : (
                    <NoRecord />
                  )}
                </tbody >
              )}
            </table >
          </div >
          <div>
            {totalPage > 1 ? (
              <Pagination
                totalPages={totalPage}
                page={page}
                key={totalPage}
                onPageClick={(page) => setPage(page + 1)}
              />
            ) : (
              ""
            )}
          </div>
        </div >
      </Wrapper >
    </>
  );
};

export default ReportedChats;
